import { unref } from 'vue'
import { saveAs } from 'file-saver'
import request from '@/utils/axios/index'
import { environment } from '@/configs/constant'
import { isString, isNullOrUndefOrEmpty, isNull, isEmptyArray } from '@/utils/is'
import useTracker from '@/hooks/useTracker'

import { ContentTypeEnum } from '@/enums/axios'
import { RoleMenuType } from '@/types/menu'
/**
 * @desc：默认分页
 * */
export const defaultPagination = {
  pageSize: 20,
  currentPage: 1,
  pageSizeOptions: [20, 30, 40, 50, 100, 200],
  layout: 'total, sizes, prev, pager, next, jumper'
}

export function getDynamicProps<T extends Record<string, unknown>, U>(props: T): Partial<U> {
  const ret: Recordable = {}

  Object.keys(props).map(key => {
    ret[key] = unref((props as Recordable)[key])
  })

  return ret as Partial<U>
}

/**
 * 导出excel
 * @param {*string} url 请求路径
 * @param {*Object} data 请求参数
 * @param {*POST | GET} method 请求方法 默认GET
 */
export function excelExport(
  url: string,
  data: any,
  method = 'POST',
  suffix = 'xlsx',
  json = false
) {
  const upperMethod = method.toUpperCase()
  if (upperMethod === 'GET') {
    return new Promise((resolve: Fn, reject) => {
      request
        .get(
          url,
          data,
          {
            responseType: 'blob',
            headers: {
              'Content-Type': json ? ContentTypeEnum.JSON : ContentTypeEnum.FORM_URLENCODED
            }
          },
          { isReturnNativeResponse: false }
        )
        .then(res => {
          const blob = new Blob([res.data])
          const name = res.filename
            ? res.filename
            : new Date().getTime() + '_' + parseInt(Math.random() * 10000) + '.' + suffix
          saveAs(blob, name)
          resolve()
        })
        .catch(_rej => {
          reject()
        })
    })
  }
  if (upperMethod === 'POST') {
    return new Promise((resolve: Fn, reject) => {
      request
        .post(
          url,
          data,
          {
            responseType: 'blob',
            headers: {
              'Content-Type': json ? ContentTypeEnum.JSON : ContentTypeEnum.FORM_URLENCODED
            }
          },
          { isReturnNativeResponse: false }
        )
        .then(res => {
          const blob = new Blob([res.data])
          const name = res.filename
            ? res.filename
            : new Date().getTime() + '_' + parseInt(Math.random() * 10000) + '.xlsx'
          saveAs(blob, name)
          resolve()
        })
        .catch(_rej => {
          reject(_rej)
        })
    })
  }
}

export function directExcelExport(
  url: string,
  data: any,
  method = 'POST',
  requestHeaderConfig?: any,
  isIgnoreErrorTip?: boolean // 是否显示【导出失败】异常提示-主要用于展示后端接口返回的提示
) {
  const upperMethod = method.toUpperCase()
  if (upperMethod === 'POST' || upperMethod === 'GET') {
    const { tracker } = useTracker()
    return new Promise((resolve: Fn, reject) => {
      const ajaxMap = {
        POST: request.post.bind(request),
        GET: request.get.bind(request)
      }
      ajaxMap[upperMethod](
        url,
        data,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': ContentTypeEnum.JSON,
            ...requestHeaderConfig
          }
        },
        { isReturnNativeResponse: false }
      )
        .then(res => {
          const blob = new Blob([res.data])
          const name = res.filename
            ? res.filename
            : new Date().getTime() + '_' + parseInt(Math.random() * 10000) + '.xlsx'
          saveAs(blob, name)
          ElMessage.success('导出成功')
          resolve()
          tracker.trackExport('EXPORT', 'SUCCESS', data.menuCode)
        })
        .catch(_rej => {
          !isIgnoreErrorTip && ElMessage.error('导出失败')
          tracker.trackExport('EXPORT', 'FAIL', data.menuCode)
          reject()
        })
    })
  }
}

/**
 * @desc:图片替换
 * @author:djsong
 * @param:picturePath 图片地址
 * @return:completePicturePath
 */
export const picturePath = (picturePath: string) => {
  if (isNullOrUndefOrEmpty(picturePath)) return ''

  if (isString(picturePath) && (picturePath as string).indexOf('http') !== -1) {
    return picturePath
  } else {
    return environment.imgBaseUrl + picturePath
  }
}
export const imgUrl = (folder: string = 'icon', key: string) => {
  return new URL(`../assets/images/${folder}/${key}`, import.meta.url).href
}
const reg =
  /^[1-9]\d{7}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}$|^[1-9]\d{5}[1-9]\d{3}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}([0-9]|X)$/
// 获取出生日期
export const getBirth = (no = '', slipt = '-', isDate = false, isReverse = false) => {
  if (!no) return no
  const toStr = no.toString()
  const yearStr = toStr.substring(6, 10)
  const dateStr = `${toStr.substring(10, 12)}${slipt}${toStr.substring(12, 14)}`
  const result = isDate ? dateStr : `${yearStr}${slipt}${dateStr}`
  return isReverse ? result.split(slipt).reverse().join(slipt) : result
}
// 获取性别

export const getSex = (no = '') => {
  if (!no) return no
  if (!reg.test(no)) return null
  const toStr = no.toString()
  if (toStr.length < 17) return no
  return Number(toStr.substring(16, 17)) % 2 ? '1' : '0'
}
// 获取年龄
export const getAge = (no = '') => {
  if (!no) return no
  if (!reg.test(no)) return ''
  const toStr = no.toString()
  const birth = new Date(getBirth(toStr))
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    age--
  }
  return age
}
export const base64ToBlob = (code: string) => {
  const parts = code.split(';base64,')
  const contentType = parts[0].split(':')[1]
  const raw = window.atob(parts[1])
  const rawLength = raw.length
  const uInt8Array = new Uint8Array(rawLength)
  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i)
  }
  return new window.Blob([uInt8Array], { type: contentType })
}
// 类型已知，不包含“;base64,”字符
export const base64ToBlob_NotBase64Str = (base64Str: string, type = 'video/mp4') => {
  const raw = window.atob(base64Str)
  const rawLength = raw.length
  const uInt8Array = new Uint8Array(rawLength)
  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i)
  }
  return new window.Blob([uInt8Array], { type })
}

export const print = function (...params: any[]) {
  Array.from(params).forEach(item => {
    if (Object.prototype.toString.call(item).slice(8, -1) === 'Object') {
      const e = [
        `%c ${item.title} %c ${item.content} `,
        'padding: 1px; border-radius: 3px 0 0 3px; color: #fff; background: #606060;',
        `padding: 1px; border-radius: 0 3px 3px 0; color: #fff; background: ${
          item.backgroundColor ? item.backgroundColor : '#1475b2'
        };`
      ]
      const fun = function (...args: any[]) {
        return (
          window.console &&
          'function' === typeof window.console.log &&
          (item = console).log.apply(e, args)
        )
      }
      // eslint-disable-next-line prefer-spread
      fun.apply(null, e)
    }
  })
}
/* 获取链接指定参数 */
export const getQueryVariable = (variable: string) => {
  if (window.location.href.indexOf('?') > -1) {
    const query = window.location.href.split('?')[1]
    const vars = query.split('&')
    for (let i = 0; i < vars.length; i++) {
      const pair = vars[i].split('=')
      if (pair[0] === variable) {
        if (pair[1] === 'undefined' || pair[1] === 'null' || pair[1] === ' ') return false
        return pair[1]
      }
    }
  } else {
    return false
  }
}
// 复制文本
export function copyText(text: string, _msg = '复制成功') {
  return new Promise(function (resolve) {
    const _textArea: any = document.createElement('textArea')
    _textArea.innerHTML = text
    _textArea.value = text
    document.body.appendChild(_textArea)
    _textArea.select()
    if (document.execCommand('Copy')) {
      resolve('success')
    }
    document.body.removeChild(_textArea)
  })
}
/**
 * 递归数组
 * @param arr(Array) 要遍历的数组
 * @param fn(function) 操作
 * @param children(String) 递归字段
 * @returns Array
 */
export const recursion = function (arr, fn, children) {
  arr.forEach(item => {
    fn && fn(item)
    if (item[children] && item[children].length) {
      recursion(item[children], fn, children)
    }
  })
}
/**
 * @description: 过滤枚举
 * @param:value 指定的值
 * @param:enums 需要过滤的枚举
 * @return:
 */
export const filterEnums = (value: unknown, enums: any[]) => {
  if (isNull(value)) {
    return {
      value: '-',
      label: '-'
    }
  }
  if (isNull(enums) || isEmptyArray(enums)) {
    return {
      value: '-',
      label: '-'
    }
  }
  const filterEnum = enums.filter(item => {
    if (String(item.value) === String(value)) return item
  })
  if (filterEnum.length > 0) return filterEnum[0]
  else {
    return {
      value: '-',
      label: '-'
    }
  }
}
/**
 * 数字转换大写金额
 * @param number int   需要转换的数字
 * @return moneyText string   转换之后的大写金额
 */
export const convertToChineseNumber = num => {
  let strOutput = ''
  let strUnit = '仟佰拾亿仟佰拾万仟佰拾元角分'
  num += '00'
  const intPos = num.indexOf('.')
  if (intPos >= 0) {
    num = num.substring(0, intPos) + num.substr(intPos + 1, 2)
  }
  strUnit = strUnit.substr(strUnit.length - num.length)
  for (let i = 0; i < num.length; i++) {
    strOutput += '零壹贰叁肆伍陆柒捌玖'.substr(num.substr(i, 1), 1) + strUnit.substr(i, 1)
  }

  return strOutput
    .replace(/零角零分$/, '整')
    .replace(/零[仟佰拾]/g, '零')
    .replace(/零{2,}/g, '零')
    .replace(/零([亿|万])/g, '$1')
    .replace(/零+元/, '元')
    .replace(/亿零{0,3}万/, '亿')
    .replace(/^元/, '零元')
}
export function accMul(arg1, arg2, precision = 2) {
  // 乘法
  let m = 0
  const s1 = arg1.toString()
  const s2 = arg2.toString()
  try {
    m += s1.split('.')[1].length
  } catch (e) {
    /* empty */
  }
  try {
    m += s2.split('.')[1].length
  } catch (e) {
    /* empty */
  }
  if (arg1 && arg2) {
    let num = (Number(s1.replace('.', '')) * Number(s2.replace('.', ''))) / Math.pow(10, m)
    try {
      num = num.toString().split('.')[1].length > precision ? Number(num.toFixed(precision)) : num
    } catch (e) {
      /* empty */
    }
    return num
  } else {
    return 0
  }
}

// 检验数字小数点后几位
export const formatNumber = (val: string, precisions: number) => {
  let temp = val.toString()
  temp = temp.replace(/[^\d.]/g, '') // 清除"数字"和"."以外的字符
  temp = temp.replace(/^\./g, '') // 验证第一个字符是数字
  temp = temp.replace(/\.{2,}/g, '') // 只保留第一个., 清除多余的
  // 输入小数位数
  if (precisions) {
    const decimal = temp.split('.')[1]
    const integer = temp.split('.')[0]
    if (decimal) {
      temp = integer + '.' + decimal.substring(0, precisions)
    }
  } else {
    temp = temp.replace(/[^\d]/g, '')
  }
  return temp
}

/**
 * 将树形结构的数据扁平化为数组
 *
 * @param treeData - 要展平的树形数据
 * @param key - 用于标识节点的键
 * @returns 一个包含所有节点的扁平数组
 */
export function flattenTree(data: any[], key = 'children') {
  return data.reduce((acc, curr) => {
    acc.push({ ...curr })
    if (curr[key] && curr[key].length > 0) {
      acc = acc.concat(flattenTree(curr[key], key))
    }
    return acc
  }, [])
}
export function jsonToUrlParam(json: Recordable) {
  return decodeURIComponent(new URLSearchParams(json).toString())
}
export function hasCode(data: any[], targetCode: string, key = 'children'): boolean {
  const flattenedData = flattenTree(data, key)
  return flattenedData.some((item: any) => item.code === targetCode)
}

// 移除所有事件监听器并清空对象的方法
export function removeAllKeyupListeners(key?: string) {
  function removeListener(k: string) {
    if (!window._CUSTOMERDETAIL_PAGE_KEYUP_EVENT_) return
    const handleKeyup = window._CUSTOMERDETAIL_PAGE_KEYUP_EVENT_[k]
    if (handleKeyup) {
      document.removeEventListener('keyup', handleKeyup)
      delete window._CUSTOMERDETAIL_PAGE_KEYUP_EVENT_[k]
    }
  }

  if (key) {
    removeListener(key)
  } else {
    for (const k in window._CUSTOMERDETAIL_PAGE_KEYUP_EVENT_) {
      removeListener(k)
    }
  }

  // 清空对象
  window._CUSTOMERDETAIL_PAGE_KEYUP_EVENT_ = {}
}
// 查找第一个包含资源列表（有权限配置）的节点
export const findFirstNodeWithResources = (nodes: any[], childrenKey: string = 'children'): any => {
  if (!Array.isArray(nodes) || nodes.length === 0) return null

  for (const node of nodes) {
    // 检查是否是末节点（没有子节点或子节点为空数组）
    const children = node[childrenKey] || node.menuList
    if (!children || !Array.isArray(children) || children.length === 0) {
      return node
    }

    // 如果有子节点，则递归查找
    const result = findFirstNodeWithResources(children, childrenKey)
    if (result) {
      return result
    }
  }

  return null
}

/**
 * 递归查找第一个叶子节点
 */
export const findFirstLeaf = (nodes: RoleMenuType[]): RoleMenuType | null => {
  if (!nodes || nodes.length === 0) return null

  for (const node of nodes) {
    // 兼容处理 children 或 menuList
    const children = node.children || node.menuList

    if (!children || children.length === 0) {
      return node
    } else {
      const leaf = findFirstLeaf(children)
      if (leaf) return leaf
    }
  }
  return null
}
